import BaseApi from '@lumapps/base-api';
import { BaseApiRequestConfig, BaseApiPromise, BaseApiMethods } from '@lumapps/base-api/types';

import { isExpired } from '../utils/isExpired';
import { getBeekeeperToken } from './token';
import type { BeekeeperApiOptions, BeekeeperTokenInfo, BeekeeperTokenResponse } from './types';

/**
 * Base Beekeeper API client class that extends BaseApi
 *
 * Handles:
 * - Beekeeper-specific authentication via token exchange
 * - Automatic token refresh when expired
 * - Dynamic baseURL configuration from Beekeeper tenant
 * - Authorization header injection
 *
 * @example
 * const formsApi = new BeekeeperApi({ path: 'api/2/forms' });
 */
export class BeekeeperApi extends BaseApi {
    /**
     * Cached Beekeeper token information
     * Shared across all BeekeeperApi instances
     */
    private static beekeeperTokenInfo: BeekeeperTokenInfo | undefined;

    /**
     * In-flight token refresh promise to prevent multiple simultaneous refresh requests
     */
    private static tokenRefreshPromise: Promise<BeekeeperTokenInfo> | undefined;

    /**
     * Default token expiration threshold -> how long before expiration we start token refresh (5 minutes)
     */
    private static readonly TOKEN_EXPIRATION_THRESHOLD = 5 * 60 * 1000;

    /**
     * HTTP methods that accept a request body/data parameter
     */
    private static readonly METHODS_WITH_DATA: ReadonlyArray<BaseApiMethods> = ['put', 'post', 'patch'];

    /**
     * Flag to track if BeekeeperApi has been initialized
     * Shared across all instances since token state is shared
     */
    private static isInitialized = false;

    /**
     * Path segment for this API instance
     */
    private readonly apiPath: string;

    constructor({ path = '', requestConfig = {} }: BeekeeperApiOptions = {}) {
        // Initialize with placeholders - will be updated after token exchange
        super({
            baseURL: '',
            path: '',
            requestConfig,
        });

        this.apiPath = path;
    }

    /**
     * Initialize Beekeeper authentication and set the correct baseURL
     * This should be called before making any API requests
     * Uses window.BeekeeperContext when available for single source of truth
     *
     * @private
     */
    private static async ensureInitialized(): Promise<void> {
        // Early return if already initialized
        if (BeekeeperApi.isInitialized) {
            return;
        }

        // If beekeeperTokenInfo doesn't exist but BeekeeperContext token does, sync from context
        if (!BeekeeperApi.beekeeperTokenInfo && window.BeekeeperContext?.token) {
            BeekeeperApi.beekeeperTokenInfo = BeekeeperApi.createTokenInfoFromContext(window.BeekeeperContext.token);
        }

        // Get or refresh the Beekeeper token only if it's missing
        if (!BeekeeperApi.beekeeperTokenInfo) {
            await BeekeeperApi.refreshBeekeeperToken();
        }

        // Mark as initialized based on token availability
        BeekeeperApi.isInitialized = !!BeekeeperApi.beekeeperTokenInfo;
    }

    /**
     * Refresh the Beekeeper token via the token exchange endpoint
     * Prevents multiple simultaneous refresh requests
     * Uses window.BeekeeperContext when available for single source of truth
     *
     * @private
     */
    private static async refreshBeekeeperToken(): Promise<BeekeeperTokenInfo> {
        // If a token refresh is already in progress, wait for it
        if (BeekeeperApi.tokenRefreshPromise) {
            return BeekeeperApi.tokenRefreshPromise;
        }

        // Check if BeekeeperContext exists and use its refresh mechanism
        if (window.BeekeeperContext?.refreshToken) {
            BeekeeperApi.tokenRefreshPromise = window.BeekeeperContext.refreshToken()
                .then((token) => {
                    // Construct token response from BeekeeperContext data
                    const tokenResponse = BeekeeperApi.createTokenInfoFromContext(token);
                    BeekeeperApi.beekeeperTokenInfo = tokenResponse;
                    BeekeeperApi.tokenRefreshPromise = undefined;
                    return tokenResponse;
                })
                .catch((error) => {
                    BeekeeperApi.tokenRefreshPromise = undefined;
                    throw error;
                });

            return BeekeeperApi.tokenRefreshPromise;
        }

        // Fallback to self-managed token (for standalone usage without BeekeeperContext)
        BeekeeperApi.tokenRefreshPromise = getBeekeeperToken()
            .then((tokenResponse) => {
                const tokenInfo = BeekeeperApi.mapResponseToTokenInfo(tokenResponse);
                BeekeeperApi.beekeeperTokenInfo = tokenInfo;
                BeekeeperApi.tokenRefreshPromise = undefined;
                return tokenInfo;
            })
            .catch((error) => {
                BeekeeperApi.tokenRefreshPromise = undefined;
                throw error;
            });

        return BeekeeperApi.tokenRefreshPromise;
    }

    /**
     * Map BeekeeperTokenResponse to BeekeeperTokenInfo
     * This provides a single point of transformation if the API response structure changes
     *
     * @private
     * @param response - Token response from API or constructed from context
     * @returns BeekeeperTokenInfo object for internal use
     */
    private static mapResponseToTokenInfo(response: BeekeeperTokenResponse): BeekeeperTokenInfo {
        return {
            token: response.token,
            defaultWebUrl: response.defaultWebUrl,
            headers: response.headers,
            expiresAt: response.expiresAt,
        };
    }

    /**
     * Construct a BeekeeperTokenInfo from window.BeekeeperContext
     * Helper to avoid duplication when syncing from context
     *
     * @private
     * @param token - Token string (can be from context or refresh)
     * @returns BeekeeperTokenInfo object
     * @throws Error if BeekeeperContext is not available
     */
    private static createTokenInfoFromContext(token: string): BeekeeperTokenInfo {
        if (!window.BeekeeperContext) {
            throw new Error('BeekeeperContext is not available');
        }

        return {
            token,
            defaultWebUrl: window.BeekeeperContext.tenantUrl,
            headers: { Authorization: `Token ${token}` },
            expiresAt: window.BeekeeperContext.expiresAt,
        };
    }

    /**
     * Compute the baseURL from current token info
     * Always computed on demand to stay in sync with shared token state
     *
     * @private
     * @returns The current baseURL for this instance
     */
    private getBaseURL(): string {
        if (!BeekeeperApi.beekeeperTokenInfo) {
            return '';
        }

        return this.apiPath
            ? `${BeekeeperApi.beekeeperTokenInfo.defaultWebUrl}/${this.apiPath}`
            : BeekeeperApi.beekeeperTokenInfo.defaultWebUrl;
    }

    /**
     * Check if the current Beekeeper token is expired or will expire soon
     * Note: If expiresAt is undefined, the token never expires (most common case)
     *
     * @private
     * @returns true if token is expired or will expire within the threshold
     */
    private static isTokenExpired(): boolean {
        // If no token info is cached, consider it expired
        if (!BeekeeperApi.beekeeperTokenInfo) {
            return true;
        }

        // If no expiration date, token never expires
        if (!BeekeeperApi.beekeeperTokenInfo.expiresAt) {
            return false;
        }

        // Check if the cached token is expired
        return isExpired(BeekeeperApi.beekeeperTokenInfo.expiresAt, BeekeeperApi.TOKEN_EXPIRATION_THRESHOLD);
    }

    /**
     * Merge request config with Beekeeper-specific headers
     * Does NOT call super.mergeConfig() to avoid CORS issues with LumApps-specific headers
     *
     * @override
     */
    // eslint-disable-next-line class-methods-use-this
    mergeConfig(config: BaseApiRequestConfig): BaseApiRequestConfig {
        // Create a clean config without LumApps headers to avoid CORS issues
        return {
            ...config,
            headers: {
                ...(config.headers ?? {}),
                Authorization: BeekeeperApi.beekeeperTokenInfo?.headers?.Authorization,
            },
        };
    }

    /**
     * Override request method to ensure initialization and token refresh
     * Completely bypasses BaseApi to avoid unwanted headers
     *
     * @override
     */
    /* eslint-disable default-param-last */
    // eslint disable is here to match the BaseApi method signature
    async request<T = any>(
        method: BaseApiMethods,
        url: string,
        config: BaseApiRequestConfig = {},
        data?: unknown,
    ): BaseApiPromise<T> {
        // Ensure the API is initialized and token is valid
        await BeekeeperApi.ensureInitialized();

        // Check if token needs refresh before making the request
        if (BeekeeperApi.isTokenExpired()) {
            await BeekeeperApi.refreshBeekeeperToken();
        }

        // Always sync baseURL from shared token state before making request
        // This ensures all instances stay in sync when token refreshes
        this.baseURL = this.getBaseURL();
        this.api.defaults.baseURL = this.baseURL;

        // Build clean config with ONLY Beekeeper headers (bypass BaseApi completely)
        const cleanConfig = this.mergeConfig(config);

        // Call axios directly instead of super.request() to avoid BaseApi's header manipulation
        if (BeekeeperApi.METHODS_WITH_DATA.includes(method)) {
            return this.api[method](url, data, cleanConfig);
        }

        return this.api[method](url, cleanConfig);
    }

    /**
     * Static method to manually clear cached token
     * Also resets initialization state so instances will reinitialize on next request
     */
    static clearToken(): void {
        BeekeeperApi.beekeeperTokenInfo = undefined;
        BeekeeperApi.tokenRefreshPromise = undefined;
        BeekeeperApi.isInitialized = false;
    }
}

export default BeekeeperApi;
